import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { waitForSession } from "@/lib/auth-ready";
import { GrantsSiteHeader } from "@/components/grants-site-header";
import { SiteFooter } from "@/components/site-footer";
import { GrantFlipCard, type GrantCardData } from "@/components/grant-flip-card";
import {
  PROGRAM_TYPES,
  deadlineDisplay,
  programTypeLabel,
  programTypeTone,
  toneClasses,
} from "@/lib/grants-utils";

type GrantsSearch = { sector?: string };

export const Route = createFileRoute("/grants/")({
  validateSearch: (s: Record<string, unknown>): GrantsSearch => ({
    sector: typeof s.sector === "string" && s.sector.trim() ? s.sector.trim() : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Startup Grants for First-Time Founders - DeckScore" },
      {
        name: "description",
        content:
          "Find early-stage startup grants you can actually apply to as a first-time founder. Score your deck free, then apply to matched programs in one form.",
      },
      { property: "og:title", content: "Startup Grants for First-Time Founders - DeckScore" },
      {
        property: "og:description",
        content:
          "Find early-stage startup grants you can actually apply to as a first-time founder. Score your deck free, then apply to matched programs in one form.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://deckscore.ai/grants" },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: "https://deckscore.ai/grants" }],
  }),
  component: GrantsListPage,
});

type Country = "India" | "Global";
type ViewMode = "cards" | "list";
type SortKey = "deadline_asc" | "deadline_desc";

function GrantsListPage() {
  const { sector: sectorFromUrl } = Route.useSearch();
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState<GrantCardData[]>([]);
  const [country, setCountry] = useState<Country>("India");
  const [view, setView] = useState<ViewMode>("cards");
  const [typeFilter, setTypeFilter] = useState<string>("all");
  const [sort, setSort] = useState<SortKey>("deadline_asc");
  const [sectorFilter, setSectorFilter] = useState<string>(sectorFromUrl ?? "all");
  

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const session = await waitForSession(4000);
      const authed = !!session;
      if (cancelled) return;
      setIsLoggedIn(authed);

      try {
        if (authed) {
          const { data } = await supabase
            .from("grants")
            .select(
              "id,name,scheme,country,regional_focus,host_organization,program_type,amount,closing_date,is_rolling,status,updated_at,eligibility,application_url,emails",
            )
            .in("status", ["open", "upcoming", "expired"])
            .order("closing_date", { ascending: true, nullsFirst: false });
          if (!cancelled) setRows((data ?? []) as GrantCardData[]);
        } else {
          const { data } = await supabase
            .from("grants_public")
            .select(
              "id,name,scheme,country,regional_focus,host_organization,program_type,amount,closing_date,is_rolling,status,updated_at,eligibility",
            )
            .in("status", ["open", "upcoming", "expired"])
            .order("closing_date", { ascending: true, nullsFirst: false });
          if (!cancelled)
            setRows(
              ((data ?? []) as unknown as GrantCardData[]).filter(
                (r) => !!r.id && !!r.name,
              ),
            );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const sectors = useMemo(() => {
    const s = new Set<string>();
    for (const r of rows) if (r["scheme" as never] || (r as any).sector_focus) {
      // sector info only reliably in DB detail; use best-effort
    }
    // We stored sector_focus on grants table; not fetched here. Skip building list.
    return Array.from(s);
  }, [rows]);

  const filtered = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return rows
      .filter((r) => {
        // status visibility rule
        if (r.status === "expired") {
          // grants_public already gates via expired_visible_until; still safe
          return true;
        }
        return r.status === "open" || r.status === "upcoming";
      })
      .filter((r) => {
        if (country === "India") return (r.country ?? "").toLowerCase().includes("india");
        return !(r.country ?? "").toLowerCase().includes("india");
      })
      .filter((r) => {
        if (typeFilter === "all") return true;
        return r.program_type === typeFilter;
      })
      .filter((r) => {
        if (sectorFilter === "all") return true;
        return ((r as any).sector_focus ?? "").toLowerCase().includes(sectorFilter.toLowerCase());
      })
      .sort((a, b) => {
        const rank = (g: GrantCardData) => {
          if (g.status === "expired") return 3;
          if (g.is_rolling) return 2;
          return 1;
        };
        const ra = rank(a);
        const rb = rank(b);
        if (ra !== rb) return ra - rb;
        const ta = a.closing_date ? new Date(a.closing_date).getTime() : Number.MAX_SAFE_INTEGER;
        const tb = b.closing_date ? new Date(b.closing_date).getTime() : Number.MAX_SAFE_INTEGER;
        return sort === "deadline_asc" ? ta - tb : tb - ta;
      });
  }, [rows, country, typeFilter, sectorFilter, sort]);

  return (
    <div className="min-h-screen bg-bg text-ink">
      <GrantsSiteHeader />
      <main className="mx-auto max-w-[1180px] px-5 py-10">
        {/* HEADER */}
        <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
          <div>
            <div
              className="text-[15px] italic text-ink-tertiary"
              style={{ fontFamily: "'Caveat', cursive" }}
            >
              verified fresh daily
            </div>
            <h1
              className="mt-1 text-[44px] font-bold leading-none tracking-tight text-ink"
              style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
            >
              Grants
            </h1>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Segmented<Country>
              options={[
                { value: "India", label: "India" },
                { value: "Global", label: "Global" },
              ]}
              value={country}
              onChange={setCountry}
            />
            <Segmented<ViewMode>
              options={[
                { value: "cards", label: "Cards" },
                { value: "list", label: "List" },
              ]}
              value={view}
              onChange={setView}
            />
          </div>
        </div>

        {/* FILTER CHIPS */}
        <div className="mt-6 flex flex-wrap items-center gap-2">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="rounded-full border border-line bg-surface px-3 py-1.5 text-[12.5px] font-medium text-ink hover:border-accent focus:outline-none"
          >
            <option value="all">All types ▾</option>
            {PROGRAM_TYPES.map((p) => (
              <option key={p.value} value={p.value}>
                {p.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => setSort(sort === "deadline_asc" ? "deadline_desc" : "deadline_asc")}
            className="rounded-full border border-line bg-surface px-3 py-1.5 text-[12.5px] font-medium text-ink hover:border-accent"
          >
            Deadline {sort === "deadline_asc" ? "↑" : "↓"}
          </button>
          <select
            value={sectorFilter}
            onChange={(e) => setSectorFilter(e.target.value)}
            className="rounded-full border border-line bg-surface px-3 py-1.5 text-[12.5px] font-medium text-ink hover:border-accent focus:outline-none"
          >
            <option value="all">Sector ▾</option>
            <option value="ai">AI</option>
            <option value="climate">Climate</option>
            <option value="health">Health</option>
            <option value="fintech">Fintech</option>
            <option value="edtech">EdTech</option>
            <option value="deeptech">Deep tech</option>
            <option value="agri">Agri</option>
          </select>

          <div className="ml-auto text-[13px] font-medium text-muted-ink">
            {loading ? "Loading…" : `${filtered.length} live programs`}
          </div>
        </div>

        {/* CONTENT */}
        <div className="mt-6">
          {!loading && filtered.length === 0 ? (
            <EmptyState />
          ) : view === "cards" ? (
            <div
              className="grid gap-5"
              style={{ gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))" }}
            >
              {filtered.map((g) => (
                <GrantFlipCard key={g.id} grant={g} isLoggedIn={isLoggedIn} />
              ))}
            </div>
          ) : (
            <ListView rows={filtered} isLoggedIn={isLoggedIn} />
          )}
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}

function Segmented<T extends string>({
  options,
  value,
  onChange,
}: {
  options: { value: T; label: string }[];
  value: T;
  onChange: (v: T) => void;
}) {
  return (
    <div className="inline-flex rounded-full border border-line bg-surface p-1">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          onClick={() => onChange(o.value)}
          className={`rounded-full px-3.5 py-1.5 text-[13px] font-semibold transition ${
            value === o.value
              ? "bg-ink text-[color:var(--color-bg)]"
              : "text-muted-ink hover:text-ink"
          }`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}


function EmptyState() {
  return (
    <div className="rounded-2xl border border-dashed border-line bg-surface p-10 text-center">
      <p className="text-[15px] font-medium text-ink">No programs match - loosen a filter</p>
      <p className="mt-2 text-[13px] text-muted-ink">
        Try switching country, clearing type, or removing quick toggles.
      </p>
    </div>
  );
}

function ListView({
  rows,
  isLoggedIn,
}: {
  rows: GrantCardData[];
  isLoggedIn: boolean;
}) {
  return (
    <div>
      <div className="grid grid-cols-[56px_2fr_1fr_1fr_1fr_1fr] gap-3 px-4 pb-2 text-[10.5px] font-bold uppercase tracking-wider text-ink-tertiary">
        <div />
        <div>Program</div>
        <div>Type</div>
        <div>Amount</div>
        <div>Deadline</div>
        <div>Apply</div>
      </div>
      <div className="flex flex-col gap-2">
        {rows.map((g) => {
          const d = deadlineDisplay(g);
          const isExpired = g.status === "expired";
          const rowBg = isExpired ? "bg-[#FFF9F1]" : "bg-surface";
          const daysBadge =
            d.variant === "rolling"
              ? "∞"
              : d.variant === "expired"
              ? "!"
              : d.daysLeft != null
              ? `${d.daysLeft}`
              : "-";
          const badgeBg =
            d.variant === "urgent"
              ? "bg-[color:var(--color-peach)]"
              : d.variant === "expired"
              ? "bg-[color:var(--color-peach)]"
              : d.variant === "rolling"
              ? "bg-[color:var(--color-accent-mint)]"
              : "bg-surface-sunken";
          const tone = programTypeTone(g.program_type);
          return (
            <Link
              key={g.id}
              to="/grants/$id"
              params={{ id: g.id }}
              className={`grid grid-cols-[56px_2fr_1fr_1fr_1fr_1fr] items-center gap-3 rounded-xl border border-line px-4 py-3 transition hover:-translate-y-0.5 hover:border-accent hover:shadow-md ${rowBg}`}
            >
              <div
                className={`flex h-11 w-11 items-center justify-center rounded-xl text-[15px] font-bold text-ink ${badgeBg}`}
              >
                {daysBadge}
              </div>
              <div className="min-w-0">
                <div
                  className="truncate text-[14.5px] font-bold text-ink"
                  style={{ fontFamily: "'Space Grotesk', system-ui, sans-serif" }}
                >
                  {g.name}
                </div>
                <div className="truncate text-[12px] text-muted-ink">
                  {[g.host_organization ?? g.scheme, g.country ?? g.regional_focus]
                    .filter(Boolean)
                    .join(" · ")}
                </div>
              </div>
              <div>
                <span
                  className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wider ${toneClasses(tone)}`}
                >
                  {programTypeLabel(g.program_type)}
                </span>
              </div>
              <div className="text-[13px] font-semibold text-ink">{g.amount ?? "-"}</div>
              <div className="text-[12.5px] text-muted-ink">{d.label}</div>
              <div>
                {isLoggedIn && g.application_url ? (
                  <a
                    href={g.application_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="inline-flex items-center rounded-full bg-[color:var(--color-accent-lavender)] px-3 py-1 text-[12px] font-bold text-ink"
                  >
                    Open →
                  </a>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-[color:var(--color-accent-lavender)] px-2.5 py-0.5 text-[11px] font-bold text-ink">
                    🔒 Free account
                  </span>
                )}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
